import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { faClose, faTicket } from "@fortawesome/free-solid-svg-icons"
import Button, { ButtonIcon } from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import { GenericDropDown } from "../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../components/Help/PopupHelpButton"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { EPlanCouponDiscountType } from "../../Enum/EPlanCouponDiscountType"
import { useResellerCoupons } from "../../hooks/Reseller/useResellerCoupons"

interface RequestResellerCouponPopupProps {
  suggestedCode: string
  onClose: () => void
  onSuccess: () => void
}

export const RequestResellerCouponPopup: React.FC<RequestResellerCouponPopupProps> = ({
  suggestedCode,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation("reseller")

  const [code, setCode] = useState(suggestedCode)

  const [discountType, setDiscountType] = useState<EPlanCouponDiscountType>(EPlanCouponDiscountType.Percentage)

  const [discountValue, setDiscountValue] = useState("10")

  const { handleRequestResellerCoupon } = useResellerCoupons()

  const discountTypeOptions = [
    { label: t("coupons.discount_type_percentage"), value: String(EPlanCouponDiscountType.Percentage) },
    { label: t("coupons.discount_type_fixed_amount"), value: String(EPlanCouponDiscountType.FixedAmount) },
  ]

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    handleRequestResellerCoupon({ code, discountType, discountValue: Number(discountValue) }, () => {
      onSuccess()
      onClose()
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={t("coupons.request_title")}
              icon={faTicket}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.reseller.requestresellercouponpopup.1"
                    variant="global"
                    title={t("helpPopup:global.resellerRequestCoupon.title")}
                    description={t("helpPopup:global.resellerRequestCoupon.description")}
                    bullets={t("helpPopup:global.resellerRequestCoupon.bullets", { returnObjects: true }) as string[]}
                    steps={t("helpPopup:global.resellerRequestCoupon.steps", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    descriptionKey="interactionDescriptions:pages.reseller.requestCouponPopup.button.onClose"
                  />
                </div>
              }
            />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <p className="p-2 mt-1 mb-4 text-sm font-semibold text-gray-500">{t("coupons.request_subtitle")}</p>
            </Animate>

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <form onSubmit={handleSubmit}>
                <div className="space-y-4 p-2">
                  <InputField
                    value={code}
                    type="text"
                    placeholder={t("coupons.label_code_placeholder")}
                    onChange={(e) => setCode(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.reseller.requestCouponPopup.input.code"
                  />

                  <GenericDropDown
                    legend={t("coupons.label_discount_type")}
                    data={discountTypeOptions}
                    initialState={String(discountType)}
                    handlerFuncion={(e) => setDiscountType(Number(e.target.value) as EPlanCouponDiscountType)}
                    descriptionKey="interactionDescriptions:pages.reseller.requestCouponPopup.select.discountType"
                  />

                  <InputField
                    value={discountValue}
                    type="number"
                    placeholder={t("coupons.label_discount_value_placeholder")}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.reseller.requestCouponPopup.input.discountValue"
                  />
                </div>

                <div className={defaultStyle.divisionBar}></div>

                <div className={defaultStyle.buttonsController}>
                  <Button
                    typeButton="submit"
                    label={t("coupons.button_request")}
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.reseller.requestCouponPopup.button.request"
                  />
                </div>
              </form>
            </Animate>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
